// Phase 17 Plan 05 Task 1 — EmpowerModal.
//
// Replacement for EmpowerBottomSheet (deleted in this plan). The new
// component matches UI-SPEC §6.2 EmpowerModal recipe — a role=dialog
// surface with a chunky-card body containing a header (title), a yellow
// ribbon ({cost} TOKEN(S) · {requirement}), a 2×2 role grid, a helper
// paragraph, and a full-width EMPOWER primary CTA. Visible strings flow
// through react-intl (FormattedMessage / intl.formatMessage) per Phase 18
// POLISH-12 i18n coverage gate.
//
// DEC-023 role costs (verified against apps/backend/src/match/economy/roles.ts):
//   knight: 1 token (any word)
//   bishop: 1 token (any word)
//   rook:   2 tokens (4+ letter word)
//   queen:  3 tokens (5+ letter word)
// King is NOT empowerable (already royalty) — not rendered in the 2×2 grid.
//
// ARCA-82 WS2 — migrated to <Dialog> primitive (ModalScrim): scrim/panel
// styling, focus trap, and dismissal contract live in the primitive.

import { type JSX, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { playSfx } from '../../game/audio/useAudio'
import type { RoleKind } from '../../game/PlayerViewSchema'
import { Btn } from '../components/Btn'
import { Dialog } from '../components/Dialog'
import { PieceGlyph } from '../components/Icon'

/** Subset of RoleKind the user can pick (king excluded — already royalty). */
export type EmpowerableRole = 'knight' | 'bishop' | 'rook' | 'queen'

/** One role's purchase terms — mirrors the backend wire shape (PlayerView.role_costs). */
export interface RoleCost {
  readonly cost: number
  readonly min_word_length: number
}

export interface EmpowerModalProps {
  readonly onClose: () => void
  readonly onConfirm: (role: EmpowerableRole) => void
  /**
   * Per-role purchase terms, sourced from the backend balance config via
   * PlayerView.role_costs. The modal NEVER hardcodes costs or word-length
   * gates — it renders exactly what the server reports. The server stays
   * authoritative (validateEmpower re-derives the cost), so this is display
   * only and cannot be spoofed into a real advantage.
   */
  readonly roleCosts: Record<EmpowerableRole, RoleCost>
  /** Caller's current token bank — gates affordability (display only). */
  readonly tokenBalance: number
  /** Length of the word claimed this turn — gates the min-word-length rule. */
  readonly wordLength: number
  readonly initialSelectedRole?: EmpowerableRole
}

const GRID_ROLES: ReadonlyArray<EmpowerableRole> = [
  'knight',
  'bishop',
  'rook',
  'queen',
]

/** Type guard so EmpowerableRole satisfies the RoleKind narrowing. */
export function isEmpowerableRole(r: RoleKind): r is EmpowerableRole {
  return r !== 'king'
}

export function EmpowerModal({
  onClose,
  onConfirm,
  roleCosts,
  tokenBalance,
  wordLength,
  initialSelectedRole,
}: EmpowerModalProps): JSX.Element {
  const intl = useIntl()
  const [selected, setSelected] = useState<EmpowerableRole | undefined>(
    initialSelectedRole,
  )

  // Escape + Tab + initial focus + restore are handled by <Dialog>/ModalScrim
  // (ARCA-82 WS2 — useFocusTrap lives in the primitive, not here).

  const isAffordable = (role: EmpowerableRole): boolean =>
    roleCosts[role].cost <= tokenBalance &&
    wordLength >= roleCosts[role].min_word_length

  const selectedCost = selected ? roleCosts[selected].cost : null
  // JUICE-3: localized ribbon. The token count + word-length requirement flow
  // through the catalog (token_cost plural + requirement select); the ribbon's
  // CSS text-transform:uppercase renders the SCREAMING caps the prototype used,
  // so we never JS-uppercase a localized string (locale-unsafe).
  const ribbonText =
    selected && selectedCost !== null
      ? `${intl.formatMessage(
          { id: 'match.empower.token_cost' },
          { count: selectedCost },
        )} · ${intl.formatMessage(
          { id: 'match.empower.requirement' },
          {
            anyWord: roleCosts[selected].min_word_length <= 3 ? 'yes' : 'no',
            min: roleCosts[selected].min_word_length,
          },
        )}`
      : intl.formatMessage({ id: 'match.empower.modal.ribbon_default' })

  return (
    // Center-dialog flavor (ARCA-82 WS2): scrim + popIn + focus trap +
    // dismissal live in <Dialog>/ModalScrim. padding:0 so the deep header
    // band bleeds to the rounded panel edge (overflow:hidden clips it).
    <Dialog
      onClose={onClose}
      labelledBy="empower-header"
      panelStyle={{ padding: 0, overflow: 'hidden' }}
    >
      {/* data-word-length is on this inner wrapper so [data-word-length]
          querySelector (element-agnostic) still finds it after migration. */}
      <div data-word-length={wordLength}>
        <div
          className="card-header"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--space-3) var(--space-4)',
            background: 'var(--color-surface-deep)',
            borderBottom: '3px solid var(--color-surface-stroke)',
          }}
        >
          <span
            className="fd"
            id="empower-header"
            style={{
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: 'var(--font-size-16)',
              color: 'var(--color-text-primary)',
            }}
          >
            <FormattedMessage id="match.empower.modal.title" />
          </span>
        </div>

        <div
          style={{
            padding: 'var(--space-3) var(--space-4) var(--space-4)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-3)',
          }}
        >
          <div
            className="ribbon yellow"
            data-testid="empower-ribbon"
            style={{
              alignSelf: 'center',
              padding: 'var(--space-1) var(--space-4)',
              background: 'var(--color-cta-primary)',
              color: 'var(--color-inkwell)',
              borderRadius: 'var(--radius-pill)',
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: 'var(--font-size-13)',
              textTransform: 'uppercase',
            }}
          >
            {ribbonText}
          </div>

          <div
            className="role-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 'var(--space-3)',
            }}
          >
            {GRID_ROLES.map((role) => {
              const isEligible = isAffordable(role)
              const isSelected = selected === role
              const roleTokenCost = roleCosts[role].cost
              const roleMinWord = roleCosts[role].min_word_length
              // EMPOWER-03 — surface the FULL purchase terms on each tile
              // (token cost + word-length gate) so a player can compare roles
              // before tapping one, instead of the cost being hidden in the
              // ribbon until selection. The word-length clause only shows when
              // it is a real constraint (min > the global 3-letter floor), so
              // Knight/Bishop stay clean.
              const costText = intl.formatMessage(
                { id: 'match.empower.token_cost' },
                { count: roleTokenCost },
              )
              const requirementText =
                roleMinWord > 3
                  ? intl.formatMessage(
                      { id: 'match.empower.requirement' },
                      { anyWord: 'no', min: roleMinWord },
                    )
                  : null
              return (
                <button
                  key={role}
                  type="button"
                  data-role={role}
                  className={`sub-card ${isSelected ? 'selected' : ''}`}
                  aria-pressed={isSelected}
                  aria-disabled={!isEligible}
                  disabled={!isEligible}
                  onClick={() => setSelected(role)}
                  style={{
                    padding: 'var(--space-3)',
                    background: isSelected
                      ? 'var(--color-surface-light)'
                      : 'var(--color-surface-mid)',
                    border: `3px solid ${
                      isSelected
                        ? 'var(--color-cta-primary)'
                        : 'var(--color-surface-stroke)'
                    }`,
                    borderRadius: 'var(--radius-md)',
                    color: 'var(--color-text-primary)',
                    opacity: isEligible ? 1 : 0.4,
                    cursor: isEligible ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 'var(--space-2)',
                  }}
                >
                  <PieceGlyph type={role} size={26} color="currentColor" />
                  <span
                    className="fd"
                    style={{
                      fontFamily: 'var(--font-family-display)',
                      fontWeight: 700,
                      fontSize: 'var(--font-size-13)',
                    }}
                  >
                    <FormattedMessage id={`match.empower.role.${role}`} />
                  </span>
                  {/* EMPOWER-01 — one-line plain-language effect so a player
                      who doesn't know chess understands what empowering to
                      this role actually DOES on the board. */}
                  <span
                    style={{
                      fontFamily: 'var(--font-family-body)',
                      fontWeight: 600,
                      fontSize: 'var(--font-size-12)',
                      lineHeight: 1.3,
                      textAlign: 'center',
                      color: 'var(--color-text-primary)',
                    }}
                  >
                    <FormattedMessage
                      id={`match.empower.role.${role}.effect`}
                    />
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--font-family-body)',
                      fontSize: 'var(--font-size-12)',
                      color: 'var(--color-text-secondary)',
                      textAlign: 'center',
                    }}
                  >
                    {requirementText === null
                      ? costText
                      : `${costText} · ${requirementText}`}
                  </span>
                </button>
              )
            })}
          </div>

          <p
            style={{
              fontFamily: 'var(--font-family-body)',
              fontSize: 'var(--font-size-12)',
              color: 'var(--color-text-secondary)',
              textAlign: 'center',
              lineHeight: 1.4,
              margin: 'var(--space-2) 0',
            }}
          >
            <FormattedMessage id="match.empower.modal.helper" />
          </p>

          <Btn
            kind="primary"
            size="lg"
            block
            disabled={!selected}
            sfx={null}
            onClick={() => {
              if (!selected) return
              // §9.6 — organ chord pitched per role (Knight low … Queen high).
              // Queen is skipped here: its QueenRises cinematic plays the
              // dedicated 'queen' signature organ so the moment isn't doubled.
              if (selected !== 'queen') {
                playSfx('empower', { pitch: GRID_ROLES.indexOf(selected) })
              }
              onConfirm(selected)
            }}
          >
            <FormattedMessage id="match.empower.cta.empower_button" />
          </Btn>
        </div>
      </div>
    </Dialog>
  )
}
